import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { addMonths } from 'date-fns';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useTypedParams } from '@sorare/routing';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { Matches } from '@sorare/gameplay/src/components/seo/Matches';

import { useLayoutData } from '#src/pages/countries/$countryCode/layout.tsx';
import { countryMessages } from '#src/pages/seo/countries/utils.tsx';

import type {
  NationalTeamFixturesQuery,
  NationalTeamFixturesQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const NATIONAL_TEAM_FIXTURES_QUERY = gql`
  query NationalTeamFixturesQuery(
    $countryCode: String!
    $cursor: String
    $futureCursor: String
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime!
  ) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        futureGames: games(
          startDate: $startDate
          endDate: $endDate
          first: 20
          after: $futureCursor
        ) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        pastGames: latestGames(first: 20, includingLive: true, after: $cursor) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${Matches.fragments.anyGameInterface}
` as TypedDocumentNode<
  NationalTeamFixturesQuery,
  NationalTeamFixturesQueryVariables
>;

const params = {
  startDate: new Date(),
  endDate: addMonths(new Date(), 2),
};

const Fixtures = () => {
  const openDialog = useOpenDialog();
  const { country } = useLayoutData();
  const { formatMessage } = useIntl();

  const displayName = formatMessage(countryMessages.nationalTeam, {
    country: country.name,
  });

  const { countryCode } = useTypedParams<PageParams>();
  const [tab, setTab] = useState<'past' | 'future'>('future');
  useTitleAndDescription(
    metadatas.clubFixtures.title,
    metadatas.clubFixtures.description,
    {
      club: displayName,
    }
  );
  const { data, loading, loadMore } = usePaginatedQuery(
    NATIONAL_TEAM_FIXTURES_QUERY,
    {
      variables: {
        countryCode: countryCode || '',
        ...params,
      },
      connection: 'GameConnection',
      skip: !countryCode,
    }
  );
  const { futureGames, pastGames } = data?.football.nationalTeam || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        futureCursor: futureGames?.pageInfo.endCursor,
        cursor: pastGames?.pageInfo.endCursor,
      });
    },
    (tab === 'future' && !!futureGames?.pageInfo.hasNextPage) ||
      (tab === 'past' && !!pastGames?.pageInfo.hasNextPage),
    loading
  );

  if (tab === 'future' && (futureGames?.nodes || []).length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <Matches
      displayName={displayName}
      loading={loading}
      pastGames={pastGames?.nodes || []}
      futureGames={futureGames?.nodes || []}
      onClick={id => openDialog(DialogKey.game, id)}
      infiniteScrollLoader={<InfiniteScrollLoader />}
      tab={tab}
      setTab={setTab}
    />
  );
};

export default Fixtures;
